/**
 * Privacy-first analytics module
 * Tracks only aggregate, anonymous statistics
 * No cookies, no localStorage, no PII
 */

import { siteConfig } from './config/site.config';
import { analyticsConfig } from './config/analytics.config';

export interface AnalyticsEvent {
  type: 'page_view' | 'app_view';
  path: string;
  appId?: string;
  timestamp: number;
}

interface QueuedEvent {
  event: AnalyticsEvent;
  retries: number;
}

class Analytics {
  private enabled: boolean;
  private queue: QueuedEvent[] = [];
  private sending: boolean = false;
  private readonly MAX_RETRIES = analyticsConfig.maxRetries;
  private readonly BATCH_SIZE = analyticsConfig.batchSize;
  private readonly FLUSH_INTERVAL = analyticsConfig.flushIntervalMs; // 5 seconds

  constructor() {
    this.enabled = siteConfig.telemetry?.enabled ?? false;

    if (this.enabled) {
      // Flush queue periodically
      setInterval(() => this.flush(), this.FLUSH_INTERVAL);

      // Flush on page unload
      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', () => this.flush(true));
      }
    }
  }

  /**
   * Track a page view
   */
  trackPageView(path: string): void {
    if (!this.enabled) return;

    const event: AnalyticsEvent = {
      type: 'page_view',
      path: this.normalizePath(path),
      timestamp: Date.now(),
    };

    this.enqueue(event);
  }

  /**
   * Track an app view
   */
  trackAppView(appId: string, path: string): void {
    if (!this.enabled) return;

    const event: AnalyticsEvent = {
      type: 'app_view',
      path: this.normalizePath(path),
      appId,
      timestamp: Date.now(),
    };

    this.enqueue(event);
  }

  /**
   * Normalize path to remove query params and ensure consistency
   */
  private normalizePath(path: string): string {
    // Remove leading/trailing slashes and query params
    return path.replace(/^\/+|\/+$/g, '').split('?')[0] || 'home';
  }

  /**
   * Add event to queue
   */
  private enqueue(event: AnalyticsEvent): void {
    this.queue.push({ event, retries: 0 });

    // Flush immediately if queue is large
    if (this.queue.length >= this.BATCH_SIZE) {
      this.flush();
    }
  }

  /**
   * Send queued events to server
   */
  private async flush(immediate: boolean = false): Promise<void> {
    if (this.sending || this.queue.length === 0) return;

    this.sending = true;

    try {
      // Take events from queue
      const eventsToSend = this.queue.splice(0, this.BATCH_SIZE);
      const events = eventsToSend.map(q => q.event);

      // Send to server
      const response = await fetch(analyticsConfig.endpoints.track, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events }),
        // Use keepalive for beforeunload
        ...(immediate && { keepalive: true }),
      });

      if (!response.ok) {
        // Re-queue failed events (with retry limit)
        const failedEvents = eventsToSend
          .filter(q => q.retries < this.MAX_RETRIES)
          .map(q => ({ ...q, retries: q.retries + 1 }));

        this.queue.unshift(...failedEvents);
      }
    } catch (error) {
      // Silently fail - analytics should never break the app
      console.debug('Analytics error:', error);
    } finally {
      this.sending = false;
    }
  }
}

// Singleton instance
export const analytics = new Analytics();

/**
 * Convenience functions for common tracking scenarios
 */
export function trackPageView(path: string): void {
  analytics.trackPageView(path);
}

export function trackAppView(appId: string, path: string): void {
  analytics.trackAppView(appId, path);
}
